/* eslint-disable @typescript-eslint/no-explicit-any */
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useFieldArray, useForm, type UseFormReturn } from 'react-hook-form';
import type { SiteBlock } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useBarbers, useBranches } from '@/lib/org';
import { ImageField } from './image-field';
import { BLOCK_LABELS, useServiceOptions, type PageSummary } from './types';

const select = 'h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs focus-visible:ring-2 focus-visible:ring-ring';

type F = UseFormReturn<any>;
const err = (f: F, name: string): string | undefined => name.split('.').reduce<any>((o, k) => o?.[k], f.formState.errors)?.message;

function FText({ f, name, label, max, hint, placeholder }: { f: F; name: string; label: string; max?: number; hint?: string; placeholder?: string }) {
  const id = `bf-${name}`;
  return (
    <Field id={id} label={label} hint={hint} error={err(f, name)}>
      <Input id={id} maxLength={max} placeholder={placeholder} aria-invalid={!!err(f, name)} {...f.register(name)} />
    </Field>
  );
}
function FArea({ f, name, label, max, rows = 4, hint }: { f: F; name: string; label: string; max: number; rows?: number; hint?: string }) {
  const id = `bf-${name}`;
  const v = (f.watch(name) as string | undefined) ?? '';
  return (
    <Field id={id} label={label} hint={hint ?? `${v.length}/${max}`} error={err(f, name)}>
      <Textarea id={id} rows={rows} maxLength={max} aria-invalid={!!err(f, name)} {...f.register(name)} />
    </Field>
  );
}
function FCheck({ f, name, label }: { f: F; name: string; label: string }) {
  const id = `bf-${name}`;
  return (
    <label htmlFor={id} className="flex items-center gap-2.5 text-sm">
      <input id={id} type="checkbox" className="size-4 accent-[var(--bottle)]" {...f.register(name)} />
      {label}
    </label>
  );
}

function CheckList({ label, items, value, onChange, empty }: { label: string; items: { id: string; name: string }[]; value: string[]; onChange: (v: string[]) => void; empty?: string }) {
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1 text-sm font-medium">{label}</legend>
      <p className="text-xs text-muted-foreground">Sem nenhum marcado, aparecem todos.</p>
      {items.length === 0 && <p className="text-sm text-muted-foreground">{empty}</p>}
      <div className="grid max-h-56 gap-1.5 overflow-y-auto rounded-md border p-3">
        {items.map((i) => (
          <label key={i.id} className="flex items-center gap-2.5 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-[var(--bottle)]"
              checked={value.includes(i.id)}
              onChange={(e) => onChange(e.target.checked ? [...value, i.id] : value.filter((x) => x !== i.id))}
            />
            {i.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function CtaFields({ f, name, label, optional }: { f: F; name: string; label: string; optional?: boolean }) {
  return (
    <fieldset className="grid gap-3 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">{label}{optional ? ' (opcional)' : ''}</legend>
      <FText f={f} name={`${name}.label`} label="Texto do botão" max={40} />
      <FText f={f} name={`${name}.href`} label="Destino" hint="Use #agendar para abrir o agendamento, ou um endereço http(s):// ou /pagina." max={2048} />
    </fieldset>
  );
}

function ServicesFields({ f }: { f: F }) {
  const services = useServiceOptions();
  const items = (services.data?.data ?? []).filter((s) => s.active && s.publicVisible);
  return (
    <>
      <FText f={f} name="title" label="Título" max={120} />
      <FText f={f} name="subtitle" label="Subtítulo" max={300} />
      <FCheck f={f} name="showPrices" label="Mostrar preços" />
      <CheckList label="Serviços exibidos" items={items} value={f.watch('serviceIds') ?? []} onChange={(v) => f.setValue('serviceIds', v, { shouldDirty: true })} empty="Cadastre serviços em Serviços para escolher aqui." />
    </>
  );
}

function BarbersFields({ f }: { f: F }) {
  const barbers = useBarbers();
  const items = (barbers.data ?? []).filter((b) => b.active).map((b) => ({ id: b.id, name: b.displayName }));
  return (
    <>
      <FText f={f} name="title" label="Título" max={120} />
      <FText f={f} name="subtitle" label="Subtítulo" max={300} />
      <CheckList label="Profissionais exibidos" items={items} value={f.watch('barberIds') ?? []} onChange={(v) => f.setValue('barberIds', v, { shouldDirty: true })} empty="Cadastre a equipe para escolher aqui." />
      <p className="text-xs text-muted-foreground">Só aparecem profissionais ativos e marcados como visíveis no site.</p>
    </>
  );
}

function GalleryFields({ f }: { f: F }) {
  const images: any[] = f.watch('images') ?? [];
  const set = (v: any[]) => f.setValue('images', v, { shouldDirty: true });
  const move = (i: number, d: number) => {
    const next = [...images];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    set(next);
  };
  return (
    <>
      <FText f={f} name="title" label="Título" max={120} />
      <div className="grid gap-3">
        {images.map((img, i) => (
          <div key={`${img.assetId ?? img.url}-${i}`} className="grid gap-2 rounded-md border p-3">
            <ImageField label={`Foto ${i + 1}`} value={img} onChange={(v) => (v ? set(images.map((x, j) => (j === i ? { ...x, ...v } : x))) : set(images.filter((_, j) => j !== i)))} purpose="gallery" />
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="icon-sm" aria-label={`Subir foto ${i + 1}`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp /></Button>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={`Descer foto ${i + 1}`} disabled={i === images.length - 1} onClick={() => move(i, 1)}><ArrowDown /></Button>
            </div>
          </div>
        ))}
        {images.length < 24 && <ImageField label="Adicionar foto" value={null} onChange={(v) => v && set([...images, { assetId: v.assetId, alt: v.alt ?? '' }])} purpose="gallery" />}
      </div>
    </>
  );
}

function LocationFields({ f }: { f: F }) {
  const branches = useBranches();
  return (
    <>
      <FText f={f} name="title" label="Título" max={120} />
      <Field id="bf-branchId" label="Unidade exibida">
        <select id="bf-branchId" className={select} {...f.register('branchId', { setValueAs: (v) => v || null })}>
          <option value="">Primeira unidade ativa</option>
          {(branches.data ?? []).filter((b) => b.active).map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </Field>
      <FCheck f={f} name="showHours" label="Mostrar horário de funcionamento" />
      <FCheck f={f} name="showMap" label="Mostrar mapa" />
    </>
  );
}

function FaqFields({ f }: { f: F }) {
  const { fields, append, remove, move } = useFieldArray({ control: f.control, name: 'items' });
  return (
    <>
      <FText f={f} name="title" label="Título" max={120} />
      <div className="grid gap-3">
        {fields.map((it, i) => (
          <div key={it.id} className="grid gap-2 rounded-md border p-3">
            <FText f={f} name={`items.${i}.q`} label={`Pergunta ${i + 1}`} max={200} />
            <FArea f={f} name={`items.${i}.a`} label="Resposta" max={1000} rows={3} />
            <div className="flex gap-1">
              <Button type="button" variant="ghost" size="icon-sm" aria-label={`Subir pergunta ${i + 1}`} disabled={i === 0} onClick={() => move(i, i - 1)}><ArrowUp /></Button>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={`Descer pergunta ${i + 1}`} disabled={i === fields.length - 1} onClick={() => move(i, i + 1)}><ArrowDown /></Button>
              <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remover pergunta ${i + 1}`} onClick={() => remove(i)}><Trash2 /></Button>
            </div>
          </div>
        ))}
        {fields.length < 20 && (
          <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => append({ q: '', a: '' })}>
            <Plus aria-hidden /> Adicionar pergunta
          </Button>
        )}
      </div>
    </>
  );
}

/** Formulário de propriedades do bloco. Remonte com `key={block.id}` ao trocar de bloco. */
export function BlockForm({ block, pages, onChange }: { block: SiteBlock; pages: PageSummary[]; onChange: (b: SiteBlock) => void }) {
  const f = useForm<any>({ defaultValues: block as any, mode: 'onChange' });

  useEffect(() => {
    const sub = f.watch((v) => {
      const next: any = { ...v, id: block.id, type: block.type };
      if (block.type === 'hero') {
        for (const k of ['primaryCta', 'secondaryCta']) if (!next[k]?.label && !next[k]?.href) delete next[k];
        if (!next.image?.assetId && !next.image?.url) delete next.image;
      }
      if (block.type === 'reviews') next.maxItems = Number(next.maxItems) || 6;
      onChange(next as SiteBlock);
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, block.id]);

  let body: ReactNode;
  switch (block.type) {
    case 'hero':
      body = (
        <>
          <FText f={f} name="eyebrow" label="Linha acima do título" max={60} />
          <FText f={f} name="title" label="Título" max={120} />
          <FArea f={f} name="subtitle" label="Subtítulo" max={300} rows={2} />
          <ImageField label="Foto de fundo" value={f.watch('image')} onChange={(v) => f.setValue('image', v ?? undefined, { shouldDirty: true })} />
          <CtaFields f={f} name="primaryCta" label="Botão principal" optional />
          <CtaFields f={f} name="secondaryCta" label="Botão secundário" optional />
        </>
      );
      break;
    case 'services':
      body = <ServicesFields f={f} />;
      break;
    case 'barbers':
      body = <BarbersFields f={f} />;
      break;
    case 'gallery':
      body = <GalleryFields f={f} />;
      break;
    case 'reviews':
      body = (
        <>
          <FText f={f} name="title" label="Título" max={120} />
          <Field id="bf-maxItems" label="Quantidade de avaliações" hint="Só aparecem avaliações com autorização de publicação (aba Avaliações)." error={err(f, 'maxItems')}>
            <Input id="bf-maxItems" type="number" min={1} max={12} {...f.register('maxItems', { valueAsNumber: true })} />
          </Field>
        </>
      );
      break;
    case 'location':
      body = <LocationFields f={f} />;
      break;
    case 'cta':
      body = (
        <>
          <FText f={f} name="title" label="Título" max={120} />
          <FArea f={f} name="body" label="Texto de apoio" max={500} rows={3} />
          <CtaFields f={f} name="button" label="Botão" />
        </>
      );
      break;
    case 'text':
      body = (
        <>
          <FText f={f} name="title" label="Título (opcional)" max={120} />
          <FArea f={f} name="body" label="Texto" max={5000} rows={10} hint="Texto puro. Cada linha vira um parágrafo; HTML não é aceito." />
        </>
      );
      break;
    case 'faq':
      body = <FaqFields f={f} />;
      break;
    case 'contact_form':
      body = (
        <>
          <FText f={f} name="title" label="Título" max={120} />
          <FText f={f} name="subtitle" label="Subtítulo" max={300} />
          <FArea f={f} name="successMessage" label="Mensagem após enviar" max={300} rows={2} />
          <Field id="bf-successPageSlug" label="Ir para a página após enviar" hint="Opcional. Se escolher, a mensagem acima não aparece.">
            <select id="bf-successPageSlug" className={select} {...f.register('successPageSlug', { setValueAs: (v) => v || null })}>
              <option value="">Ficar na mesma página</option>
              {pages.filter((p) => p.slug !== '' && p.slug.length >= 3).map((p) => (
                <option key={p.id} value={p.slug}>{p.title}</option>
              ))}
            </select>
          </Field>
          <p className="text-xs text-muted-foreground">As mensagens chegam na aba Mensagens e por e-mail, se o envio estiver configurado.</p>
        </>
      );
      break;
  }

  return (
    <form className="grid gap-4" onSubmit={(e) => e.preventDefault()} aria-label={`Propriedades: ${BLOCK_LABELS[block.type]}`}>
      {body}
    </form>
  );
}
